// Who the other side of a transaction was, from one address's point of view.
//
// The wallet page listed "1,000.00000000 BTC received" and never said received
// from whom, which is the question an address explorer exists to answer. The
// data was already there and unrendered: blockchain.info's multiaddr returns
// full `inputs` and `out` arrays per transaction, the same shape as rawtx, so
// this costs no extra request.
//
// Which side to read depends on the direction. Coins arriving means the payers
// are the input addresses; coins leaving means the recipients are the output
// addresses. Either way the wallet being viewed is dropped from its own list -
// it funds its own spends and takes its own change back, and "sent to itself"
// is not what somebody reading the row wants to know.
//
// Imports nothing, so it stays cheap to test and cheap to move.

// An address can fund a transaction twice, and a batch can pay the same address
// more than once. Both are ordinary and neither is two counterparties, so the
// list is deduplicated while keeping the order the chain reports.
const distinct = (addresses) => {
  const seen = new Set();
  return addresses.filter((address) => {
    if (!address || seen.has(address)) return false;
    seen.add(address);
    return true;
  });
};

// A coinbase - the first transaction in every block, where the miner is paid -
// has a single input that spends nothing: no address and no value. Every mining
// pool payout address on this site has these all over its history, and without
// the check they read as a payment from nobody.
const isCoinbase = (tx) =>
  (tx.inputs || []).length === 1 &&
  !tx.inputs[0]?.prev_out?.addr &&
  !tx.inputs[0]?.prev_out?.value;

// `own` is every address the page is looking up, as a Set - not one address.
// The explorer takes pipe-delimited lookups, so a page can be several addresses
// at once, and each one is change to the others rather than a counterparty.
// Passing the URL segment instead would make a two-address lookup list half of
// itself as the other side of its own transactions.
//
// Returns:
//   { kind: "coinbase" }                    newly mined coins, no payer
//   { kind: "self" }                        nothing left the wallet's own control
//   { kind: "unknown" }                     no address on that side could be read
//   { kind: "addresses", addresses: [...] } everyone on the other side
//
// `addresses` is never empty for kind "addresses", so a caller can render it
// without a length check.
export const counterpartiesOf = (tx, own) => {
  const incoming = (tx?.result ?? 0) >= 0;

  if (incoming && isCoinbase(tx)) return { kind: "coinbase" };

  const side = incoming
    ? (tx?.inputs || []).map((input) => input?.prev_out?.addr)
    : (tx?.out || []).map((output) => output?.addr);

  // Outputs with no address are OP_RETURN or a non-standard script, and inputs
  // with none could not be decoded. Neither names a counterparty, so both fall
  // out here rather than being rendered as a blank.
  const readable = distinct(side);

  // Nothing on that side could be read at all. Distinct from a self-send: there
  // is no evidence the coins stayed with the wallet, so the row says nothing
  // rather than claiming they did.
  if (readable.length === 0) return { kind: "unknown" };

  const others = readable.filter((address) => !own.has(address));

  // Every address on the other side was this wallet's own. A consolidation, a
  // change-only spend, or one of the OP_RETURN self-sends that make up much of
  // the chain's traffic - all of which moved nothing to anyone else.
  if (others.length === 0) return { kind: "self" };

  return { kind: "addresses", addresses: others };
};
